import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is a third-party npm package, not a local convex/ directory import
import { api } from '@convex/_generated/api';
import { useOrgContext } from '@/hooks/useOrgContext';
import type { IUseLeadsBoardReturn, IBoardStageDef, TBoardLead } from '@/@types/leadsBoard';
import type { TLeadStatus } from '@/@types/leads';

export const INSURANCE_STAGES: readonly IBoardStageDef[] = [
  { status: 'new', label: 'New' },
  { status: 'contacted', label: 'Contacted' },
  { status: 'qualified', label: 'Qualified' },
  { status: 'quote_sent', label: 'Quote Sent' },
  { status: 'negotiation', label: 'Negotiation' },
  { status: 'closed_won', label: 'Won' },
  { status: 'closed_lost', label: 'Lost' },
  { status: 'nurture', label: 'Nurture' },
];

export const REAL_ESTATE_STAGES: readonly IBoardStageDef[] = [
  { status: 'new', label: 'New' },
  { status: 'contacted', label: 'Contacted' },
  { status: 'qualified', label: 'Qualified' },
  { status: 'appointment_booked', label: 'Appt Booked' },
  { status: 'showing_scheduled', label: 'Showing' },
  { status: 'offer_made', label: 'Offer Made' },
  { status: 'under_contract', label: 'Under Contract' },
  { status: 'closed_won', label: 'Closed Won' },
  { status: 'closed_lost', label: 'Closed Lost' },
  { status: 'nurture', label: 'Nurture' },
];

/**
 * Buckets leads by pipeline stage — one bucket per stage in the given order.
 * Leads whose status has no matching column are omitted. Input order is
 * preserved within a bucket (api.leads.list is already lastMessageAt-sorted).
 */
export function groupLeadsByStage(
  leads: readonly TBoardLead[],
  stages: readonly IBoardStageDef[]
): Array<{ stage: IBoardStageDef; leads: TBoardLead[] }> {
  return stages.map(stage => ({
    stage,
    leads: leads.filter(lead => lead.status === stage.status),
  }));
}

export function useLeadsBoard(): IUseLeadsBoardReturn {
  const { effectiveIndustry } = useOrgContext();
  const stages = effectiveIndustry === 'insurance' ? INSURANCE_STAGES : REAL_ESTATE_STAGES;

  const rawLeads = useQuery(api.leads.list, {});
  const isLoading = rawLeads === undefined;
  const leads = useMemo(() => rawLeads ?? [], [rawLeads]);

  const updateStatus = useMutation(api.leads.updateStatus).withOptimisticUpdate(
    (localStore, args) => {
      const existing: TBoardLead[] | undefined = localStore.getQuery(api.leads.list, {});
      if (existing === undefined) return;

      localStore.setQuery(
        api.leads.list,
        {},
        existing.map((lead: TBoardLead) =>
          lead._id === args.leadId ? { ...lead, status: args.status } : lead
        )
      );
    }
  );

  const moveLead = useCallback(
    (leadId: TBoardLead['_id'], toStatus: TLeadStatus) => {
      void updateStatus({ leadId, status: toStatus });
    },
    [updateStatus]
  );

  const columns = useMemo(() => groupLeadsByStage(leads, stages), [leads, stages]);

  return { columns, stages, isLoading, moveLead };
}
